import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Shield, 
  ClipboardCheck, 
  BarChart3, 
  FileText, 
  GraduationCap, 
  AlertTriangle,
  Users,
  Wifi,
  Lock,
  Eye,
  ArrowRight,
  CheckCircle,
  Star
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { getUserTypeLabel as getTypeLabel } from '../utils/userTypeSync'

const Home = () => {
  const { user, profile } = useAuth()
  
  const getUserTypeLabel = () => {
    return getTypeLabel(profile?.user_type) || 'User'
  }

  const features = [
    {
      icon: ClipboardCheck,
      title: 'Cyber Readiness Self-Check',
      description: 'Answer simple yes/no questions to assess your agency\'s cybersecurity level.',
      color: 'cyber',
      link: '/assessment'
    },
    {
      icon: BarChart3,
      title: 'Readiness Score & Action Tips',
      description: 'Get a risk score (Low, Medium, High) with simple steps to improve.',
      color: 'success',
      link: '/dashboard'
    },
    {
      icon: FileText,
      title: 'Policy & Template Library',
      description: 'Download ready-to-use templates like Data Privacy Policies and Consent Forms.',
      color: 'warning',
      link: '/policy-library'
    },
    {
      icon: GraduationCap,
      title: 'Cyber Tips & Learning Corner',
      description: 'Access short videos and infographics about phishing, passwords, and safe practices.',
      color: 'cyber',
      link: '/learning-center'
    },
    {
      icon: AlertTriangle,
      title: 'Incident Reporter',
      description: 'Quickly report suspicious activity to your IT or DICT team.',
      color: 'security',
      link: '/incident-reporter'
    },
    {
      icon: Users,
      title: 'User Profiles',
      description: 'Choose between DPO, IT, or Admin view for a customized experience.',
      color: 'gov',
      link: '/profile'
    }
  ]

  const stats = [
    { label: 'Government Offices', value: '1,500+', icon: Shield },
    { label: 'Assessments Completed', value: '25,000+', icon: ClipboardCheck },
    { label: 'Security Improvements', value: '85%', icon: CheckCircle },
    { label: 'User Satisfaction', value: '4.8/5', icon: Star }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cyber-900 via-gov-800 to-cyber-900 text-white overflow-hidden">
        {/* Background Animation */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-cyber-400 rounded-full opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, -100],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center items-center space-x-4 mb-6">
              <Shield className="w-16 h-16 text-cyber-400" />
              <Lock className="w-12 h-12 text-success-400" />
              <Eye className="w-10 h-10 text-warning-400" />
              <Wifi className="w-8 h-8 text-cyber-300" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 text-shadow">
              CyberGov <span className="text-cyber-400">Check</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-cyber-200 mb-8 max-w-3xl mx-auto">
              Simple cybersecurity assessment tools designed for Philippine government offices. 
              Improve your cyber hygiene and governance compliance with ease.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              {user ? (
                <>
                  <Link
                    to="/assessment"
                    className="btn-primary text-lg px-8 py-4 flex items-center space-x-2 cyber-glow"
                  >
                    <ClipboardCheck size={20} />
                    <span>Start Assessment</span>
                    <ArrowRight size={16} />
                  </Link>
                  
                  <Link
                    to="/dashboard"
                    className="btn-secondary text-lg px-8 py-4 flex items-center space-x-2"
                  >
                    <BarChart3 size={20} />
                    <span>View Dashboard</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="btn-primary text-lg px-8 py-4 flex items-center space-x-2 cyber-glow"
                  >
                    <Users size={20} />
                    <span>Create Account</span>
                    <ArrowRight size={16} />
                  </Link>
                  
                  <Link
                    to="/login"
                    className="btn-secondary text-lg px-8 py-4 flex items-center space-x-2"
                  >
                    <Lock size={20} />
                    <span>Sign In</span>
                  </Link>
                </>
              )}
            </div>
            
            {/* User Status */}
            {user && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="glass-effect rounded-lg p-4 max-w-md mx-auto"
              >
                <p className="text-sm text-cyber-200 mb-2">
                  Welcome back, <span className="font-semibold">{profile?.name || user?.email}</span>
                </p>
                <p className="text-xs text-cyber-300">
                  {getUserTypeLabel()} • {profile?.agency || 'Not specified'}
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center"
                >
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 bg-cyber-100 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-cyber-600" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold text-gov-800 mb-2">{stat.value}</h3>
                  <p className="text-gov-600 font-medium">{stat.label}</p>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-gov-50 to-cyber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-display font-bold text-gov-800 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-gov-600 max-w-3xl mx-auto">
              Everything you need to assess, improve, and maintain your organization's cybersecurity posture.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="card group cursor-pointer"
                >
                  <Link to={feature.link} className="block">
                    <div className={`w-12 h-12 bg-${feature.color}-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                      <Icon className={`w-6 h-6 text-${feature.color}-600`} />
                    </div>
                    <h3 className="text-xl font-semibold text-gov-800 mb-3 group-hover:text-cyber-600 transition-colors duration-200">
                      {feature.title}
                    </h3>
                    <p className="text-gov-600 mb-4">
                      {feature.description}
                    </p>
                    <div className="flex items-center text-cyber-600 font-medium group-hover:translate-x-2 transition-transform duration-200">
                      <span>Learn More</span>
                      <ArrowRight size={16} className="ml-2" />
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-cyber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-display font-bold text-white mb-6">
              Ready to Secure Your Agency?
            </h2>
            <p className="text-xl text-cyber-100 mb-8">
              Join thousands of government offices already using CyberGov Check to improve their cybersecurity posture.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <>
                  <Link
                    to="/assessment"
                    className="bg-white text-cyber-600 hover:bg-cyber-50 font-semibold py-4 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <ClipboardCheck size={20} />
                    <span>Start Your Assessment</span>
                  </Link>
                  <Link
                    to="/dashboard"
                    className="border-2 border-white text-white hover:bg-white hover:text-cyber-600 font-semibold py-4 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <BarChart3 size={20} />
                    <span>View Dashboard</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="bg-white text-cyber-600 hover:bg-cyber-50 font-semibold py-4 px-8 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Users size={20} />
                    <span>Get Started Today</span>
                  </Link>
                  <Link
                    to="/about"
                    className="border-2 border-white text-white hover:bg-white hover:text-cyber-600 font-semibold py-4 px-8 rounded-lg transition-colors duration-200"
                  >
                    Learn More About Us
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home